/**
 * Reporting Core SDK - API Client
 *
 * Framework-agnostic client for the Reporting API.
 */

import type {
  ApiResponse,
  AuthProviderConfig,
  ExportMetadata,
  FilterOptionsData,
  GenerateReportRequest,
  HtmlResponse,
  ListReportsData,
  PaginationData,
  PollOptions,
  ReportConfig,
  ReportData,
  ReportingClientConfig,
  ReportRunMetadata,
} from './types';
import { ReportingApiError } from './types';

const DEFAULT_BASE_URL = 'http://localhost:4000/api/v1';

/**
 * Check if provider is a token-based auth provider.
 */
function isTokenProvider(
  provider: AuthProviderConfig
): provider is { getAccessToken: () => Promise<string> | string } {
  return 'getAccessToken' in provider;
}

/**
 * Core Reporting API client.
 *
 * Wraps the user reporting endpoints with:
 * - Automatic auth header injection
 * - Response unwrapping
 * - Consistent error handling
 *
 * @example
 * ```ts
 * const client = createReportingClient({
 *   baseUrl: 'https://api.example.com/api/v1',
 *   authProvider: {
 *     getAccessToken: () => myAuthStore.getToken(),
 *   },
 * });
 *
 * const reports = await client.listReports();
 * ```
 */
export interface ReportingClient {
  /**
   * List all available reports.
   * GET /api/v1/reports
   */
  listReports(): Promise<ListReportsData>;

  /**
   * Get report configuration including filters.
   * GET /api/v1/reports/{reportId}/config
   */
  getReportConfig(reportId: string): Promise<ReportConfig>;

  /**
   * Get filter options for a query-backed filter.
   * GET /api/v1/reports/{reportId}/filters/{filterId}/options
   *
   * @param reportId - Report ID
   * @param filterId - Filter ID
   * @param dependencyValues - Current values of dependency filters (for cascading)
   */
  getFilterOptions(
    reportId: string,
    filterId: string,
    dependencyValues?: Record<string, unknown>
  ): Promise<FilterOptionsData>;

  /**
   * Generate a report with the given parameters.
   * POST /api/v1/reports/{reportId}/generate
   *
   * Returns either:
   * - JSON data (format: 'html' with renderHtml: false or unset)
   * - HTML response with pagination (format: 'html' with renderHtml: true)
   * - Export metadata with download URL (format: 'csv' | 'xlsx' | 'pdf')
   */
  generateReport(
    reportId: string,
    request: GenerateReportRequest
  ): Promise<ReportData | HtmlResponse | ExportMetadata>;

  /**
   * Generate a report and return JSON data (convenience method).
   */
  generateReportJson(
    reportId: string,
    filters: Record<string, unknown>,
    options?: { page?: number; pageSize?: number }
  ): Promise<ReportData>;

  /**
   * Generate a report and return HTML preview with pagination metadata (convenience method).
   */
  generateReportHtml(
    reportId: string,
    filters: Record<string, unknown>,
    options?: { page?: number; pageSize?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }
  ): Promise<HtmlResponse>;

  /**
   * Generate a CSV export and return download metadata (convenience method).
   */
  generateReportCsv(
    reportId: string,
    filters: Record<string, unknown>,
    options?: { pageSize?: number }
  ): Promise<ExportMetadata>;

  /**
   * Generate an XLSX export and return download metadata (convenience method).
   */
  generateReportXlsx(
    reportId: string,
    filters: Record<string, unknown>,
    options?: { pageSize?: number }
  ): Promise<ExportMetadata>;

  /**
   * Generate a PDF export and return download metadata (convenience method).
   */
  generateReportPdf(
    reportId: string,
    filters: Record<string, unknown>,
    options?: { pageSize?: number }
  ): Promise<ExportMetadata>;

  // ============================================================================
  // Async Report Run Methods
  // ============================================================================

  /**
   * Generate a report asynchronously.
   * Returns immediately with run metadata for polling.
   * POST /api/v1/reports/{reportId}/generate with executionMode: 'async'
   */
  generateReportAsync(
    reportId: string,
    request: Omit<GenerateReportRequest, 'executionMode'>
  ): Promise<ReportRunMetadata>;

  /**
   * Get the status of an async report run.
   * GET /api/v1/reports/runs/{runId}
   */
  getReportRunStatus(runId: string): Promise<ReportRunMetadata>;

  /**
   * Get the result of a completed async report run.
   * GET /api/v1/reports/runs/{runId}/result
   * Only for data/html runs (not exports).
   */
  getReportRunResult(runId: string): Promise<ReportData | HtmlResponse>;

  /**
   * Poll an async run until completion and return the final result.
   *
   * For export runs, returns the run metadata with downloadUrl.
   * For data/html runs, fetches and returns the result.
   */
  pollReportRunUntilComplete(
    runId: string,
    options?: PollOptions
  ): Promise<ReportData | HtmlResponse | ReportRunMetadata>;

  /**
   * Generate a report asynchronously and wait for completion.
   * Combines generateReportAsync + pollReportRunUntilComplete.
   *
   * This is the easiest way to generate large reports that might
   * exceed synchronous timeout limits.
   */
  generateReportAsyncAndWait(
    reportId: string,
    request: Omit<GenerateReportRequest, 'executionMode'>,
    options?: PollOptions
  ): Promise<ReportData | HtmlResponse | ReportRunMetadata>;
}

/**
 * Create a new Reporting API client.
 */
export function createReportingClient(config: ReportingClientConfig): ReportingClient {
  const baseUrl = config.baseUrl ?? DEFAULT_BASE_URL;
  const authProvider = config.authProvider;

  /**
   * Get auth headers from the provider.
   */
  async function getAuthHeaders(): Promise<Record<string, string>> {
    if (isTokenProvider(authProvider)) {
      const token = await authProvider.getAccessToken();
      return { Authorization: `Bearer ${token}` };
    }
    return await authProvider.getAuthHeaders();
  }

  /**
   * Extract pagination metadata from response headers.
   */
  function extractPaginationFromHeaders(headers: Headers): PaginationData | undefined {
    const currentPage = headers.get('X-Report-Current-Page');
    const totalPages = headers.get('X-Report-Total-Pages');
    const totalRecords = headers.get('X-Report-Total-Records');
    const pageSize = headers.get('X-Report-Page-Size');

    // Only return pagination if all headers are present
    if (currentPage && totalPages && totalRecords && pageSize) {
      return {
        currentPage: parseInt(currentPage, 10),
        totalPages: parseInt(totalPages, 10),
        totalRecords: parseInt(totalRecords, 10),
        pageSize: parseInt(pageSize, 10),
      };
    }

    return undefined;
  }

  /**
   * Make an authenticated request and unwrap the response.
   */
  async function request<T>(
    endpoint: string,
    options: RequestInit = {},
    acceptedStatusCodes: number[] = [200]
  ): Promise<T> {
    const authHeaders = await getAuthHeaders();

    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
    });

    // Handle HTML responses (for renderHtml: true)
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('text/html')) {
      if (!response.ok) {
        throw new ReportingApiError('Request failed', {
          statusCode: response.status,
        });
      }

      const html = await response.text();
      const pagination = extractPaginationFromHeaders(response.headers);

      // Return HtmlResponse with pagination metadata
      return {
        html,
        pagination,
      } as T;
    }

    // Parse JSON response
    let body: ApiResponse<T>;
    try {
      body = await response.json();
    } catch {
      throw new ReportingApiError('Failed to parse response', {
        statusCode: response.status,
      });
    }

    // Handle error responses
    // Accept if status is in accepted list OR response.ok is true (for compatibility with tests)
    const isAccepted =
      acceptedStatusCodes.includes(response.status) ||
      (response.ok && acceptedStatusCodes.includes(200));
    if (!isAccepted || body.success === false) {
      const errorBody = body as {
        success: false;
        error: string;
        message: string;
        details?: Record<string, unknown>;
      };
      throw new ReportingApiError(errorBody.message || 'Request failed', {
        statusCode: response.status,
        errorCode: errorBody.error,
        details: errorBody.details,
      });
    }

    // Unwrap success response
    return body.data;
  }

  /**
   * Sleep for a given duration.
   */
  function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  return {
    async listReports(): Promise<ListReportsData> {
      return request<ListReportsData>('/reports');
    },

    async getReportConfig(reportId: string): Promise<ReportConfig> {
      return request<ReportConfig>(`/reports/${encodeURIComponent(reportId)}/config`);
    },

    async getFilterOptions(
      reportId: string,
      filterId: string,
      dependencyValues?: Record<string, unknown>
    ): Promise<FilterOptionsData> {
      let endpoint = `/reports/${encodeURIComponent(reportId)}/filters/${encodeURIComponent(filterId)}/options`;

      // Add dependency values as query parameters
      if (dependencyValues && Object.keys(dependencyValues).length > 0) {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(dependencyValues)) {
          if (value !== undefined && value !== null) {
            params.set(key, String(value));
          }
        }
        const queryString = params.toString();
        if (queryString) {
          endpoint += `?${queryString}`;
        }
      }

      return request<FilterOptionsData>(endpoint);
    },

    async generateReport(
      reportId: string,
      generateRequest: GenerateReportRequest
    ): Promise<ReportData | HtmlResponse | ExportMetadata> {
      return request<ReportData | HtmlResponse | ExportMetadata>(
        `/reports/${encodeURIComponent(reportId)}/generate`,
        {
          method: 'POST',
          body: JSON.stringify(generateRequest),
        }
      );
    },

    async generateReportJson(
      reportId: string,
      filters: Record<string, unknown>,
      options?: { page?: number; pageSize?: number }
    ): Promise<ReportData> {
      const result = await this.generateReport(reportId, {
        filters,
        format: 'html',
        renderHtml: false,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? 50,
      });

      if (typeof result === 'string') {
        throw new ReportingApiError('Unexpected HTML response', {
          errorCode: 'UNEXPECTED_RESPONSE',
        });
      }

      return result as ReportData;
    },

    async generateReportHtml(
      reportId: string,
      filters: Record<string, unknown>,
      options?: { page?: number; pageSize?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }
    ): Promise<HtmlResponse> {
      const result = await this.generateReport(reportId, {
        filters,
        format: 'html',
        renderHtml: true,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? 50,
        sortBy: options?.sortBy,
        sortOrder: options?.sortOrder,
      });

      // Check if result is HtmlResponse
      if (typeof result === 'object' && 'html' in result) {
        return result as HtmlResponse;
      }

      throw new ReportingApiError('Expected HTML response', {
        errorCode: 'UNEXPECTED_RESPONSE',
      });
    },

    async generateReportCsv(
      reportId: string,
      filters: Record<string, unknown>,
      options?: { pageSize?: number }
    ): Promise<ExportMetadata> {
      const result = await this.generateReport(reportId, {
        filters,
        format: 'csv',
        page: 1,
        pageSize: options?.pageSize ?? 10000,
      });

      if (typeof result === 'string') {
        throw new ReportingApiError('Unexpected HTML response for CSV export', {
          errorCode: 'UNEXPECTED_RESPONSE',
        });
      }

      // Export response includes downloadUrl at top level
      const exportResult = result as ExportMetadata;
      if (!exportResult.downloadUrl) {
        throw new ReportingApiError('Missing download URL in CSV export response', {
          errorCode: 'MISSING_DOWNLOAD_URL',
        });
      }

      return exportResult;
    },

    async generateReportXlsx(
      reportId: string,
      filters: Record<string, unknown>,
      options?: { pageSize?: number }
    ): Promise<ExportMetadata> {
      const result = await this.generateReport(reportId, {
        filters,
        format: 'xlsx',
        page: 1,
        pageSize: options?.pageSize ?? 10000,
      });

      if (typeof result === 'string') {
        throw new ReportingApiError('Unexpected HTML response for XLSX export', {
          errorCode: 'UNEXPECTED_RESPONSE',
        });
      }

      // Export response includes downloadUrl at top level
      const exportResult = result as ExportMetadata;
      if (!exportResult.downloadUrl) {
        throw new ReportingApiError('Missing download URL in XLSX export response', {
          errorCode: 'MISSING_DOWNLOAD_URL',
        });
      }

      return exportResult;
    },

    async generateReportPdf(
      reportId: string,
      filters: Record<string, unknown>,
      options?: { pageSize?: number }
    ): Promise<ExportMetadata> {
      const result = await this.generateReport(reportId, {
        filters,
        format: 'pdf',
        page: 1,
        pageSize: options?.pageSize ?? 10000,
      });

      if (typeof result === 'string') {
        throw new ReportingApiError('Unexpected HTML response for PDF export', {
          errorCode: 'UNEXPECTED_RESPONSE',
        });
      }

      // Export response includes downloadUrl at top level
      const exportResult = result as ExportMetadata;
      if (!exportResult.downloadUrl) {
        throw new ReportingApiError('Missing download URL in PDF export response', {
          errorCode: 'MISSING_DOWNLOAD_URL',
        });
      }

      return exportResult;
    },

    // =========================================================================
    // Async Report Run Methods
    // =========================================================================

    async generateReportAsync(
      reportId: string,
      generateRequest: Omit<GenerateReportRequest, 'executionMode'>
    ): Promise<ReportRunMetadata> {
      return request<ReportRunMetadata>(
        `/reports/${encodeURIComponent(reportId)}/generate`,
        {
          method: 'POST',
          body: JSON.stringify({
            ...generateRequest,
            executionMode: 'async',
          }),
        },
        [202] // Accept 202 Accepted
      );
    },

    async getReportRunStatus(runId: string): Promise<ReportRunMetadata> {
      return request<ReportRunMetadata>(`/reports/runs/${encodeURIComponent(runId)}`);
    },

    async getReportRunResult(runId: string): Promise<ReportData | HtmlResponse> {
      return request<ReportData | HtmlResponse>(
        `/reports/runs/${encodeURIComponent(runId)}/result`
      );
    },

    async pollReportRunUntilComplete(
      runId: string,
      options?: PollOptions
    ): Promise<ReportData | HtmlResponse | ReportRunMetadata> {
      const pollIntervalMs = options?.pollIntervalMs ?? 2000;
      const maxWaitMs = options?.maxWaitMs ?? 600000; // 10 minutes
      const startTime = Date.now();

      let lastStatus: ReportRunMetadata | null = null;

      while (Date.now() - startTime < maxWaitMs) {
        const status = await this.getReportRunStatus(runId);
        lastStatus = status;

        // Notify callback if provided
        options?.onStatusChange?.(status);

        // Check for terminal states
        if (status.status === 'succeeded') {
          // For export runs, return the metadata with downloadUrl
          if (status.downloadUrl) {
            return status;
          }
          // For data/html runs, fetch the result
          return this.getReportRunResult(runId);
        }

        if (status.status === 'failed') {
          throw new ReportingApiError(status.error?.message ?? 'Report run failed', {
            errorCode: status.error?.code ?? 'REPORT_RUN_FAILED',
            details: status.error?.details,
          });
        }

        // Wait before polling again
        await sleep(pollIntervalMs);
      }

      // Timeout - throw error with last known status
      throw new ReportingApiError('Report run timed out waiting for completion', {
        errorCode: 'POLL_TIMEOUT',
        details: {
          runId,
          lastStatus: lastStatus?.status,
          maxWaitMs,
        },
      });
    },

    async generateReportAsyncAndWait(
      reportId: string,
      generateRequest: Omit<GenerateReportRequest, 'executionMode'>,
      options?: PollOptions
    ): Promise<ReportData | HtmlResponse | ReportRunMetadata> {
      const run = await this.generateReportAsync(reportId, generateRequest);
      return this.pollReportRunUntilComplete(run.runId, options);
    },
  };
}
